import * as Api from '@ViewModels';
import { escapeHtml } from '../queries/Admin/Calendar/textToHtmlUtils';
import { convertHtmlStringValueToPlainTextLines } from './utils/stringUtils';

/** Where a saved prompt is offered. The API sends it as a string. */
export enum AIPromptUsage {
	EmailCampaign = 'EmailCampaign',
	EmailExecAssistant = 'EmailExecAssistant',
	Texting = 'Texting',
	AutomationSegment = 'AutomationSegment',
	ContentRefine = 'ContentRefine',
	ContentCalendar = 'ContentCalendar',
	ImageGeneration = 'ImageGeneration',
	MeetingPrep = 'MeetingPrep',
	KeepInTouch = 'KeepInTouch',
	BrandVoice = 'BrandVoice',
	CoffeePin = 'CoffeePin',
	CallAnalysis = 'CallAnalysis',
	RepCoaching = 'RepCoaching',
	EmailExecAssistantIgnore = 'EmailExecAssistantIgnore',
	KeepInTouchResearch = 'KeepInTouchResearch',
	KeepInTouchNotes = 'KeepInTouchNotes',
	AssistantChat = 'AssistantChat',
}

/**
 * A saved starting point for an AI campaign brief, as the API returns it. Curated prompts and an account's own are
 * the same shape; what separates them is scope, which only the server sets. The prompt itself is the template's
 * content: read it with {@link getAIPromptText}.
 */
export interface IAIPromptTemplate {
	id: string;
	name: string;
	summary?: string;
	content?: Api.IRawRichTextContentState;
	usage: AIPromptUsage;
	keywords?: string[];
	industries?: string[];
	scope?: Api.TemplateScope;
	creator?: Api.IUserReference;
}

/** What a save sends. Scope is the only way to ask for a prompt the rest of the account can see. */
export interface ISaveAIPromptTemplate {
	name: string;
	content: Api.IRawRichTextContentState;
	usage: AIPromptUsage;
	scope: Api.TemplateScope;
}

export const isAccountScopedPrompt = (prompt: IAIPromptTemplate) => prompt.scope === Api.TemplateScope.Account;

/** The prompt as the plain text the goal field holds, line breaks kept. */
export const getAIPromptText = (prompt: IAIPromptTemplate) =>
	convertHtmlStringValueToPlainTextLines(
		Api.getContentHtmlStringValueFromRawRichTextContentState(prompt.content)
	).trim();

/**
 * The content a prompt is saved as. Escaped rather than built with createRawRichTextContentStateWithText, which
 * writes the text into markup as-is: a prompt saying "<first name>" would be saved as a tag and lose the phrase.
 */
export const createAIPromptContent = (text: string): Api.IRawRichTextContentState => ({
	document: `<${Api.RawRichTextContentDocumentRootElementName}>${escapeHtml(text)}</${Api.RawRichTextContentDocumentRootElementName}>`,
	documentVersion: 1,
});

/**
 * Narrows a loaded set by search term. The industry cut happens server side; this is only the text box, so that
 * typing filters what is already on screen rather than waiting on a round trip.
 */
export const filterAIPrompts = (prompts: IAIPromptTemplate[], search?: string) => {
	const term = search?.trim().toLowerCase() ?? '';
	if (!term) {
		return prompts;
	}
	return prompts.filter(prompt =>
		[prompt.name, prompt.summary, getAIPromptText(prompt), ...(prompt.keywords ?? [])]
			.filter(Boolean)
			.join(' ')
			.toLowerCase()
			.includes(term)
	);
};

const PLACEHOLDER_PATTERN = /\[[^\]\n]+\]/;

/**
 * Where a prompt's first [bracketed placeholder] sits, so it can be selected and typed over as soon as the prompt is
 * used; null when the prompt has none.
 */
export const findPromptPlaceholder = (text: string): { end: number; start: number } | null => {
	const match = PLACEHOLDER_PATTERN.exec(text);
	return match ? { end: match.index + match[0].length, start: match.index } : null;
};

/**
 * Every distinct [bracketed placeholder] in a prompt. Recorded when the prompt is used, so the brief can hold the user
 * back only on the prompt's own blanks and never on brackets they typed themselves.
 */
export const findPromptPlaceholders = (text: string): string[] => [
	...new Set(text.match(new RegExp(PLACEHOLDER_PATTERN.source, 'g')) ?? []),
];
